const MENTION_POPUP_CLASS = "asset-comment-mention-popup";

/** Tags the popup element so the open check can find it. */
export const markMentionPopup = (element: HTMLElement) => {
  element.classList.add(MENTION_POPUP_CLASS);
};

const isMentionPopupInDom = () =>
  document.querySelector(`.${MENTION_POPUP_CLASS}`) !== null;

/**
 * Whether an `@`-mention popup was open when the in-flight keydown started.
 *
 * Escape unwinds one layer at a time — the popup, then the open comment, then
 * comment mode — but only the innermost layer stops the event, so the outer
 * layers have to recognize the inner ones and stand down.
 *
 * The popup can't be detected by looking for it: TipTap dismisses it from a
 * ProseMirror keydown handler on the composer's own node, and that tears the
 * element out of the DOM synchronously, mid-dispatch. Any handler further out
 * that runs on the bubble phase therefore sees the key *after* the popup is
 * already gone, and can't tell that press apart from one with nothing open.
 *
 * So the answer is recorded on the capture phase, before the event reaches
 * ProseMirror, and read back during the same event.
 */
export const wasMentionPopupOpen = (event: KeyboardEvent) =>
  openDuring.get(event) ?? isMentionPopupInDom();

/**
 * Snapshots per event rather than in a single variable, so a stale `true` from
 * an earlier keypress can never suppress a later one.
 */
const openDuring = new WeakMap<KeyboardEvent, boolean>();

/**
 * Starts recording popup state for every keydown, on the capture phase.
 *
 * On `window`, the outermost target, so the snapshot is written before any
 * handler that might read it — including the comment overlay's own
 * capture-phase listener, which also sits on `window` but registers later.
 *
 * Idempotent and never torn down: one listener that only writes to a WeakMap,
 * cheaper than the bookkeeping to install and remove it around each composer.
 */
export const trackMentionPopupState = () => {
  if (isTracking) {
    return;
  }

  isTracking = true;
  window.addEventListener(
    "keydown",
    (event) => {
      openDuring.set(event, isMentionPopupInDom());
    },
    true,
  );
};

let isTracking = false;
